import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageHeader, Segmented } from '@/ui'
import { useAuth } from '@/components/AuthGate.js'
import { useFeatures } from '@/lib/hooks.js'
import { FinancePage } from './FinancePage.js'
import { AccountingPage } from './AccountingPage.js'
import { OperatingCostsTab } from '@/components/finance/OperatingCostsTab.js'
import { OtherIncomeTab } from '@/components/finance/OtherIncomeTab.js'

type TabValue = 'overview' | 'expenses' | 'costs' | 'income'

/**
 * Accounting hub page showing Finance (profit/cash report), Accounting (expenses/VAT),
 * and in advanced mode, Operating Costs and Other Income in one tabbed interface.
 * Tab selection is stored in the URL search param `tab`.
 *
 * Tab visibility is controlled by staff section access:
 * - 'overview' (Finance) visible if admin OR has 'finance' section
 * - 'expenses' (Accounting) visible if admin OR has 'accounting' section
 * - 'costs' (Operating Costs) visible if admin OR has 'accounting' section, advanced mode only
 * - 'income' (Other Income) visible if admin OR has 'accounting' section, advanced mode only
 *
 * If only one tab is visible, the Segmented control is hidden and that tab
 * is shown directly.
 */
export function AccountingHubPage() {
  const { t } = useTranslation()
  const { account } = useAuth()
  const { advancedAccounting } = useFeatures()
  const [searchParams, setSearchParams] = useSearchParams()

  const isAdmin = account.role === 'admin'
  const hasFinance = account.sections.includes('finance')
  const hasAccounting = account.sections.includes('accounting')

  // Determine which tabs are visible
  const visibleTabs = useMemo(() => {
    const tabs: TabValue[] = []
    if (isAdmin || hasFinance) tabs.push('overview')
    if (isAdmin || hasAccounting) tabs.push('expenses')
    if ((isAdmin || hasAccounting) && advancedAccounting) {
      tabs.push('costs')
      tabs.push('income')
    }
    return tabs
  }, [isAdmin, hasFinance, hasAccounting, advancedAccounting])

  // Get the active tab from URL or default to the first visible tab
  const tabParam = searchParams.get('tab') as TabValue | null
  const activeTab: TabValue | undefined =
    tabParam && visibleTabs.includes(tabParam) ? tabParam : visibleTabs[0]

  // Ensure we have at least one tab
  if (visibleTabs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center max-w-md">
          <h1 className="text-display-md text-on-surface mb-2">
            {t('auth.noAccessTitle')}
          </h1>
          <p className="text-body-lg text-on-surface-variant mb-6">
            {t('auth.noAccessBody')}
          </p>
          <a href="/" className="text-primary hover:underline font-semibold">
            Go to dashboard
          </a>
        </div>
      </div>
    )
  }

  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('tab', value)
    setSearchParams(newParams)
  }

  const tabOptions = [
    { value: 'overview' as TabValue, label: t('accounting.tabs.overview') },
    { value: 'expenses' as TabValue, label: t('accounting.tabs.expenses') },
    { value: 'costs' as TabValue, label: t('accounting.advanced.tabs.costs') },
    { value: 'income' as TabValue, label: t('accounting.advanced.tabs.income') },
  ].filter(opt => visibleTabs.includes(opt.value))

  const currentTab = activeTab ?? visibleTabs[0] ?? 'overview'

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('accounting.hubTitle')}
        subtitle={t('accounting.hubSubtitle')}
      />

      {/* Show tab control only if multiple tabs are visible */}
      {visibleTabs.length > 1 && (
        <div className="flex items-center justify-between">
          <Segmented
            options={tabOptions}
            value={currentTab}
            onChange={handleTabChange}
            label={t('accounting.title')}
            className="no-scrollbar"
          />
        </div>
      )}

      {/* Render only the active tab */}
      {currentTab === 'overview' && <FinancePage embedded />}
      {currentTab === 'expenses' && <AccountingPage embedded />}
      {currentTab === 'costs' && <OperatingCostsTab />}
      {currentTab === 'income' && <OtherIncomeTab />}
    </div>
  )
}
